<template>
    <div v-if="!hasAnswer" class="survey-receive-text">
        <!-- we need **.lazy** for v-focus to prevent losing focus after @input -->
        <textarea
            v-model="answerText"
            v-autoexpand-height
            autofocus
            aria-label="Your answer to the question"
            class="survey-receive-text--input"
            @keypress="onKeyPress"
        />
        <button
            type="submit"
            class="survey-receive-text--button"
            aria-label="Submit Answer"
            :disabled="!answerText"
            @click="submitReplyText"
        >
            <img
                src="../../../../assets/img/Icons/White/plane.svg"
                alt="Submit Answer icon"
            />
        </button>
    </div>
    <div v-else class="survey-receive-text--answer">
        <span>{{ answerText }}</span>
    </div>
</template>

<script lang="ts" setup>
import { onMounted, ref } from 'vue'
import { ReceiveText, SurveyDisplayValues } from '@/entities/conversation'

interface Props {
    item: ReceiveText
    surveyDisplayValues?: SurveyDisplayValues
}

const props = defineProps<Props>()
const emit = defineEmits(['callNext'])

const answerText = ref('')
const hasAnswer = ref(false)

onMounted(() => {
    if (props.surveyDisplayValues) {
        const savedAnswer = props.surveyDisplayValues[
            props.item?.param
        ] as string
        if (savedAnswer) {
            hasAnswer.value = true
            answerText.value = savedAnswer
        }
    }
})

const submitReplyText = (evt) => {
    evt.preventDefault()
    hasAnswer.value = true
    const newValue = { [props.item.param]: answerText.value }
    emit('callNext', newValue, answerText.value)
}

const onKeyPress = (evt) => {
    if (evt.keyCode === 13 && evt.shiftKey) {
        submitReplyText(evt)
    }
}
</script>

<style lang="less">
.survey-receive-text {
    position: relative;
    text-align: right;
    padding: 5px;

    &--input {
        padding: 12px 60px 12px 20px;
        border-radius: 20px;
        border: 1px solid #bbb;
        min-width: 250px;
        max-width: 90%;
        resize: none;
        font-family: Arial, Helvetica, sans-serif;
        color: #bbb;
        font-size: 1em;
        -ms-overflow-style: none;

        &::-webkit-scrollbar {
            display: none;
        }

        &:focus {
            outline: none;
            border: 1px solid var(--brandColor);
            color: #000;
        }

        &.red {
            border: 1px solid #ff4c3b;
        }
    }

    &--button {
        position: absolute;
        background-color: var(--brandColor);
        border: none;
        width: 32px;
        height: 32px;
        border-radius: 16px;
        z-index: 1;
        bottom: 20px;
        right: 16px;
        cursor: pointer;
        text-align: center;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background-color 0.3s;

        img {
            width: 18px;
            margin-top: 2px;
            display: inline-block;
        }

        &:focus {
            outline: none;
        }

        &:disabled {
            background-color: #d6d6d6;
        }
    }

    &--answer {
        font-family: Roboto, Arial, Helvetica, sans-serif;
        background-color: #eee;
        border-radius: 20px 0px 20px 20px;
        padding: 12px 20px;
        margin-left: 100px;
        align-self: flex-end;
        width: fit-content;

        span {
            white-space: pre-wrap;
        }
    }
}

/* Desktop overrides */
@media only screen and (min-width: 600px) {
    .survey-receive-text {
        &--input {
            padding: 15px 60px 15px 30px;
            border-radius: 30px;
            font-size: 1em;
            width: 400px;
        }
        &--button {
            bottom: 17px;
            right: 16px;
        }
    }
}

/* IE 10+ rules */
@media screen\0 {
    .survey-receive-text {
        &--button {
            bottom: 14px;
            right: 14px;
        }
    }
}
</style>
